import React from "react";
import {
    TextField,
    Switch,
    Box,
    Autocomplete,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import { useField, useFormikContext } from "formik";

/**
 * CUANDO SE MUESTRA EL ERROR
 *
 * Solo despues del primer intento de SUBMIT. Nunca por el blur.
 *
 * `meta.touched` se enciende al SALIR de un campo, asi que alcanzaba con pasar por
 * arriba de un input sin llenarlo para que apareciera "campo requerido", cuando el
 * usuario todavia no habia terminado de cargar el formulario. Con `submitCount` el
 * formulario se queda callado hasta que la persona dice que termino.
 *
 * Despues del primer submit el error SI se actualiza mientras corrige, porque
 * `validateOnChange` sigue activo: se apaga solo al arreglar el campo, sin tener que
 * volver a mandar.
 *
 * Es el unico lugar donde se decide esto. Los ~50 `<Formik>` de la app no participan:
 * no hace falta tocar ninguno, ni pasarles `validateOnBlur={false}` (la validacion
 * puede seguir corriendo en el blur, simplemente no se muestra).
 */
export const useErrorAlSubmit = (meta) => {
    const { submitCount } = useFormikContext();
    return submitCount > 0 ? meta.error : undefined;
};

/**
 * ESPACIO RESERVADO PARA EL ERROR
 *
 * MUI solo dibuja el <p> del helper si `helperText` es truthy. Sin eso, el texto de
 * error aparece de la nada y empuja todo lo de abajo: el form salta al validar.
 *
 * Por eso el fallback es `" "` (un espacio) en vez de `undefined`: el renglon existe
 * siempre, y el error solo lo llena. Un `min-height` por CSS no alcanza, porque sin
 * texto el elemento directamente no se renderiza.
 *
 * Quien pase su propio `helperText` lo sigue viendo igual.
 *
 * Y quien quiera RENUNCIAR al espacio reservado puede pasar `helperText=""`: el `??`
 * solo atrapa null/undefined, asi que la cadena vacia llega tal cual a MUI y ahi no
 * dibuja el <p>. Sirve para formularios sin validacion, donde el renglon nunca se va a
 * llenar y solo ocupa alto.
 */
export function FormikTextInput({
    className,
    disabled,
    fullWidth = true,
    helperText,
    id,
    InputLabelProps,
    InputProps,
    labelText,
    name,
    placeholder,
    defaultValue,
    type,
    readOnly=false,
    ...props
}) {
    const [field, meta] = useField(name);
    const error = useErrorAlSubmit(meta);

    return (
        <>
            <TextField
                className={className}
                disabled={disabled}
                error={Boolean(error)}
                fullWidth={fullWidth}
                id={id}
                helperText={
                    error ?? (helperText ?? " ")
                }
                InputLabelProps={{
                    ...InputLabelProps,
                    style: { fontSize: "1.3em" },
                    shrink: true,
                }}
                InputProps={{
                    ...InputProps,
                    autoComplete: "off",
                    readOnly: readOnly,
                }}
                label={labelText}
                margin="dense"
                name={name}
                placeholder={placeholder}
                defaultValue={defaultValue}
                type={type}
                variant="standard"
                {...field}
                {...props}
            />
        </>
    );
}

export function FormikNumberInput({
    className,
    disabled,
    fullWidth = true,
    helperText,
    id,
    InputLabelProps,
    InputProps,
    labelText,
    name,
    placeholder,
    defaultValue,
    type,
    ...props
}) {
    const [field, meta] = useField(name);
    const error = useErrorAlSubmit(meta);

    return (
        <>
            <TextField
                className={className}
                disabled={disabled}
                error={Boolean(error)}
                fullWidth={fullWidth}
                id={id}
                helperText={
                    error ?? (helperText ?? " ")
                }
                InputLabelProps={{
                    ...InputLabelProps,
                    style: { fontSize: "1.3em" },
                    shrink: true,
                }}
                InputProps={{
                    ...InputProps,
                    autoComplete: "false",
                }}
                label={labelText}
                margin="dense"
                name={name}
                placeholder={placeholder}
                defaultValue={defaultValue}
                type={"number"}
                variant="standard"
                {...field}
                {...props}
            />
        </>
    );
}

export function FormikSelectInput({
    children,
    className,
    disabled,
    fullWidth = true,
    helperText,
    id,
    labelText,
    name,
    defaultValue,
    ...props
}) {
    const [field, meta] = useField(name);
    const error = useErrorAlSubmit(meta);
    return (
        <>
            <TextField
                className={className}
                defaultValue={defaultValue}
                disabled={disabled}
                error={Boolean(error)}
                fullWidth={fullWidth}
                helperText={
                    error ?? (helperText ?? " ")
                }
                id={id}
                InputLabelProps={{
                    style: { fontSize: "1.3rem" },
                    shrink: true,
                }}
                label={labelText}
                margin="dense"
                name={name}
                select
                SelectProps={{ native: true }}
                variant="standard"
                {...field}
                {...props}
            >
                {children}
            </TextField>
        </>
    );
}


/**
 * Checkbox atado a Formik que guarda "Y"/"N", NO booleanos.
 *
 * Es la convencion del modulo para las banderas —`rnhAgenteEstablecimientoUnico`,
 * `rnhLugarPrincipal`, `rnhTituloRequiereMatricula`, `rnhCargoPuedeEditar`—, asi que el
 * valor que queda en Formik ya es el que espera el servicio y el submit no traduce nada.
 *
 * NO confundir con `FormikYesOrNoInput`, que esta mas abajo: aquel es un Switch atado a
 * booleanos, que ningun servicio de este modulo acepta.
 *
 * SIN RENGLON DE ERROR, a diferencia del resto de los inputs de este archivo: un checkbox
 * no tiene estado invalido —siempre vale "Y" o "N"— asi que reservarle el hueco solo
 * desalinearia la fila.
 *
 * El `marginLeft` neutraliza los -11px con que MUI corre el FormControlLabel para
 * compensar el padding del checkbox. Quedan los 5px del `size="small"`, y el cuadradito
 * arranca a ras del borde izquierdo de la columna, alineado con los inputs de arriba.
 */
export function FormikCheckboxInput({ name, labelText, disabled = false }) {
    const [field, , helpers] = useField(name);

    return (
        <FormControlLabel
            sx={{ marginLeft: "-5px" }}
            control={
                <Checkbox
                    size="small"
                    checked={field.value === "Y"}
                    disabled={disabled}
                    onChange={(e) => helpers.setValue(e.target.checked ? "Y" : "N")}
                />
            }
            label={labelText}
        />
    );
}

export const FormikYesOrNoInput = ({ labelText, name }) => {
    const [field] = useField(name);

    return (
        <Box display={"flex"}>
            <p style={{ margin: "5px" }}>No</p>
            <Switch name="requiereFirma"{...field}
                checked={field.value === true}
                value={field.value}
            />
            <p style={{ margin: "5px" }}>Si</p>
        </Box>
    );
}

export function FormikDateInput({
    className,
    disabled,
    fullWidth = true,
    helperText,
    id,
    InputLabelProps,
    InputProps,
    labelText,
    name,
    placeholder,
    defaultValue,
    type,
    ...props
}) {
    const [field, meta] = useField(name);
    const error = useErrorAlSubmit(meta);

    return (
        <>
            <TextField
                type="date"
                className={className}
                disabled={disabled}
                error={Boolean(error)}
                fullWidth={fullWidth}
                id={id}
                helperText={
                    error ?? (helperText ?? " ")
                }
                InputLabelProps={{
                    ...InputLabelProps,
                    style: { fontSize: "1.3em" },
                    shrink: true,
                }}
                InputProps={{
                    ...InputProps,
                    autoComplete: "false",
                }}
                label={labelText}
                margin="dense"
                name={name}
                placeholder={placeholder}
                defaultValue={defaultValue}
                variant="standard"
                {...field}
                {...props}
            />
        </>
    );
}

/**
 * Select con buscador, para listas largas donde recorrer un desplegable no escala.
 *
 * Es el hermano de FormikSelectInput y se cambia uno por otro: mismo `name`, misma
 * etiqueta, mismo renglon reservado para el error.
 *
 * FORMIK SIGUE GUARDANDO EL ID, no el objeto. Es lo que hace que sea un reemplazo
 * directo: el resto del formulario (la validacion, el payload del servicio, cualquier
 * efecto que mire el valor) no se entera del cambio. MUI necesita el objeto, asi que la
 * traduccion id -> opcion pasa aca adentro.
 *
 *   <FormikAutocompleteInput
 *       name="localidadId"
 *       labelText="Localidad"
 *       options={localidades}
 *       optionValue="localidadId"
 *       optionLabel="localidadNombre"
 *   />
 *
 * El `placeholder` es lo unico que separa los dos usos que tiene hoy, y los dos buscan
 * igual:
 *
 *   - "Buscar localidad..." -> listas largas, donde el buscador ES el punto.
 *   - "Seleccione"          -> listas cortas que antes eran un desplegable. Se lee como
 *                              el select de siempre y el buscador queda de yapa.
 *
 * No hace falta un componente por cada uno: cambia el texto del campo vacio, nada mas.
 */
export function FormikAutocompleteInput({
    className,
    disabled,
    fullWidth = true,
    helperText,
    labelText,
    name,
    options = [],
    optionValue = "id",
    optionLabel = "label",
    placeholder = "Buscar...",
    ...props
}) {
    const [field, meta] = useField(name);
    const { setFieldValue } = useFormikContext();
    const error = useErrorAlSubmit(meta);

    // `String(...)` a los dos lados: los catalogos mezclan ids numericos con valores de
    // formik que viajan como texto, y sin normalizar el valor precargado no matchea
    // ninguna opcion y el campo abre vacio.
    const seleccionada =
        options.find((o) => String(o[optionValue]) === String(field.value)) ?? null;

    return (
        <Autocomplete
            className={className}
            disabled={disabled}
            fullWidth={fullWidth}
            options={options}
            value={seleccionada}
            onChange={(_evento, opcion) =>
                setFieldValue(name, opcion ? opcion[optionValue] : "")
            }
            getOptionLabel={(opcion) => String(opcion?.[optionLabel] ?? "")}
            isOptionEqualToValue={(opcion, valor) =>
                String(opcion[optionValue]) === String(valor?.[optionValue])
            }
            noOptionsText="Sin resultados"
            // MUI rotula en ingles los dos iconos del campo ("Clear", "Open"/"Close").
            // Son los tooltips y el texto que lee un lector de pantalla, asi que van
            // traducidos aca y no en cada uso.
            clearText="Limpiar"
            openText="Abrir"
            closeText="Cerrar"
            renderInput={(params) => (
                <TextField
                    {...params}
                    error={Boolean(error)}
                    // Mismo contrato que el resto: el renglon existe siempre para que el
                    // error no empuje el formulario al aparecer.
                    helperText={error ?? (helperText ?? " ")}
                    InputLabelProps={{
                        ...params.InputLabelProps,
                        style: { fontSize: "1.3em" },
                        shrink: true,
                    }}
                    label={labelText}
                    margin="dense"
                    placeholder={placeholder}
                    variant="standard"
                />
            )}
            {...props}
        />
    );
}
